import { useRef, useState } from "react";
import { ChevronsLeftRight } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { beforeAfter } from "@/data/content";

export const BeforeAfter = () => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const update = (clientX) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  };

  return (
    <section className="bg-card" data-testid="before-after-section">
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">
              Před a po
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              {beforeAfter.title}
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">
              Stejný dům, stejný pohled. Posuňte úchytku a porovnejte původní
              stav fasády s dokončenou realizací.
            </p>
            <p className="mt-6 text-sm text-muted-foreground">{beforeAfter.caption}</p>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <div
              ref={ref}
              data-testid="before-after-slider"
              className="relative mx-auto aspect-[3/5] w-full max-w-[400px] cursor-col-resize touch-none select-none overflow-hidden rounded-md lg:mx-0 lg:ml-auto"
              onPointerDown={(e) => {
                dragging.current = true;
                e.currentTarget.setPointerCapture(e.pointerId);
                update(e.clientX);
              }}
              onPointerMove={(e) => dragging.current && update(e.clientX)}
              onPointerUp={() => (dragging.current = false)}
              onPointerCancel={() => (dragging.current = false)}
            >
              {/* Before (base layer) */}
              <img
                src={beforeAfter.before}
                alt="Původní stav fasády"
                className="absolute inset-0 h-full w-full object-cover"
                draggable="false"
              />
              {/* After (clipped from the right) */}
              <img
                src={beforeAfter.after}
                alt="Dokončená fasáda po rekonstrukci"
                className="absolute inset-0 h-full w-full object-cover"
                style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
                draggable="false"
              />
              {/* Labels */}
              <span className="absolute left-4 top-4 rounded-sm bg-primary/85 px-2.5 py-1 text-xs font-semibold text-primary-foreground">
                Po
              </span>
              <span className="absolute right-4 top-4 rounded-sm bg-background/85 px-2.5 py-1 text-xs font-semibold text-foreground">
                Před
              </span>
              {/* Handle */}
              <div
                className="absolute inset-y-0 flex w-px items-center bg-background/90"
                style={{ left: `${pos}%` }}
              >
                <span
                  data-testid="before-after-handle"
                  className="absolute left-1/2 flex h-11 w-11 -translate-x-1/2 items-center justify-center rounded-full border border-border bg-background text-foreground shadow-soft"
                >
                  <ChevronsLeftRight className="h-5 w-5" />
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
